"use client"

import * as React from "react"
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog"

import { cn } from "@productlane/lib/src/browser"

import type { ButtonProps } from "../button"
import { Button } from "../button"

export const InternalAlertDialog = AlertDialogPrimitive.Root

export const InternalAlertDialogTrigger = AlertDialogPrimitive.Trigger

export const InternalAlertDialogPortal = ({
	...props
}: AlertDialogPrimitive.AlertDialogPortalProps) => (
	<AlertDialogPrimitive.Portal {...props} />
)

export const InternalAlertDialogOverlay = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<AlertDialogPrimitive.Overlay
		className={cn(
			"fixed inset-0 z-50 bg-gray-950/10 backdrop-blur-sm !duration-150 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in dark:bg-gray-900/80",
			className,
		)}
		{...props}
		ref={ref}
	/>
))

export const InternalAlertDialogContent = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
	<InternalAlertDialogPortal>
		<InternalAlertDialogOverlay />
		<AlertDialogPrimitive.Content
			ref={ref}
			className={cn(
				"fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-white p-6 shadow-lg outline-none !duration-150 ease-out focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:fade-in data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-3xl md:w-full dark:bg-gray-900",
				className,
			)}
			{...props}
		/>
	</InternalAlertDialogPortal>
))

export const InternalAlertDialogHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col space-y-2 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
)

export const InternalAlertDialogFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			"flex flex-col-reverse items-center sm:flex-row sm:items-stretch sm:justify-end sm:space-x-2",
			className,
		)}
		{...props}
	/>
)

export const InternalAlertDialogTitle = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
	<AlertDialogPrimitive.Title
		ref={ref}
		className={cn("text-lg font-semibold", className)}
		{...props}
	/>
))

export const InternalAlertDialogDescription = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
	<AlertDialogPrimitive.Description
		ref={ref}
		className={cn("text-sm text-secondary", className)}
		{...props}
	/>
))

export const InternalAlertDialogAction = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Action>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> &
		ButtonProps
>(({ className, children, ...props }, ref) => (
	<AlertDialogPrimitive.Action ref={ref} asChild {...props}>
		<Button className={cn(className)} size="lg">
			{children}
		</Button>
	</AlertDialogPrimitive.Action>
))

export const InternalAlertDialogCancel = React.forwardRef<
	React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
	React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> &
		ButtonProps
>(({ className, children, ...props }, ref) => (
	<AlertDialogPrimitive.Cancel ref={ref} {...props} asChild>
		<Button
			className={cn("mt-2 sm:mt-0", className)}
			variant="outline"
			size="lg"
		>
			{children}
		</Button>
	</AlertDialogPrimitive.Cancel>
))
